import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL as API } from '@/config/api';
import { Button } from '@/components/ui/button';

const TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const LIMIT = 8 * 1024 * 1024;

export default function RestaurantLogoUpload({ restaurantId }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(true);
  const [uncertain, setUncertain] = useState(true);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);
  const [broken, setBroken] = useState(false);
  const fileInput = useRef(null);
  const session = useRef(null);
  const pending = useRef(false);

  useEffect(() => {
    const current = { id: restaurantId, controller: new AbortController() };
    session.current = current;
    pending.current = false;
    setUrl(null); setBroken(false); setBusy(true); setUncertain(true); setError('');
    if (restaurantId) {
      axios.get(`${API}/restaurants/${restaurantId}`, {
        withCredentials: true, signal: current.controller.signal,
      }).then(({ data }) => {
        if (session.current !== current) return;
        setUrl(data.logo_url || null); setUncertain(false);
      }).catch(() => {
        if (session.current === current) setError('Unable to load logo. Reload before making changes.');
      }).finally(() => {
        if (session.current === current) setBusy(false);
      });
    } else {
      setBusy(false);
    }
    return () => {
      session.current = null;
      current.controller.abort();
    };
  }, [restaurantId]);

  const reload = async (current) => {
    const { data } = await axios.get(`${API}/restaurants/${current.id}`, {
      withCredentials: true, signal: current.controller.signal,
    });
    if (session.current !== current) return;
    setUrl(data.logo_url || null); setBroken(false); setUncertain(false);
  };

  const refresh = async () => {
    const current = session.current;
    if (!current?.id || pending.current) return;
    pending.current = true; setBusy(true);
    try { await reload(current); if (session.current === current) setError(''); }
    catch { if (session.current === current) setError('Unable to load logo. Please retry.'); }
    finally { if (session.current === current) { pending.current = false; setBusy(false); } }
  };

  const mutate = async (file) => {
    const current = session.current;
    if (!current?.id || pending.current || uncertain) return;
    if (file && (!TYPES.includes(file.type) || file.size > LIMIT)) {
      setError(file.size > LIMIT ? 'Image must be 8 MiB or smaller.' : 'Choose JPEG, PNG or static WebP.');
      return;
    }
    pending.current = true; setBusy(true); setError(''); setProgress(null);
    const options = { withCredentials: true, signal: current.controller.signal };
    try {
      let response;
      const endpoint = `${API}/restaurants/${current.id}/logo`;
      if (file) {
        const body = new FormData(); body.append('file', file);
        response = await axios.put(endpoint, body, { ...options, onUploadProgress: event => {
          if (session.current === current) setProgress(event.total ? Math.round(event.loaded / event.total * 100) : null);
        } });
      } else {
        response = await axios.delete(endpoint, options);
      }
      if (session.current !== current) return;
      setUrl(response.data.logo_url || null); setBroken(false);
    } catch (failure) {
      if (session.current !== current) return;
      const status = failure.response?.status;
      const definiteRejection = [400, 401, 403, 404, 413, 415, 422].includes(status);
      const detail = failure.response?.data?.detail;
      setError(definiteRejection && typeof detail === 'string' ? detail
        : 'Logo change could not be confirmed. Reloading the current logo.');
      if (!definiteRejection) {
        setUncertain(true);
        try {
          await reload(current);
          if (session.current === current) setError('Current logo reloaded. Review it before trying again.');
        } catch {
          if (session.current === current) setError('Unable to confirm logo. Reload before making further changes.');
        }
      }
    } finally {
      if (session.current === current) { pending.current = false; setBusy(false); setProgress(null); }
    }
  };

  return <div className="space-y-3" aria-busy={busy}>
    <div className="font-medium">Restaurant Logo</div>
    {url && !broken ? <img key={url} src={url} alt="Restaurant logo"
      className="h-24 max-w-full object-contain rounded border border-border/40"
      onError={() => setBroken(true)} /> : <p className="text-sm text-muted-foreground">
      {broken ? 'Logo preview unavailable.' : 'No logo to preview.'}
    </p>}
    <input ref={fileInput} aria-label="Choose restaurant logo" type="file" className="sr-only"
      accept={TYPES.join(',')} disabled={busy || uncertain || !restaurantId}
      onChange={event => {
        const file = event.target.files?.[0]; event.target.value = '';
        if (file) mutate(file);
      }} />
    <div className="flex gap-2">
      <Button type="button" disabled={busy || uncertain || !restaurantId} onClick={() => fileInput.current?.click()}>
        {url ? 'Replace Logo' : 'Upload Logo'}
      </Button>
      {url && <Button type="button" variant="outline" disabled={busy || uncertain} onClick={() => mutate(null)}>Remove Logo</Button>}
      {uncertain && !busy && restaurantId && <Button type="button" variant="outline" onClick={refresh}>Reload Logo</Button>}
    </div>
    <p className="text-sm text-muted-foreground">JPEG, PNG or static WebP. Maximum 8 MiB. Transparent logos supported.</p>
    {busy && <p role="status">{progress === null ? 'Loading or saving logo?' : `Uploading logo: ${progress}%`}</p>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
  </div>;
}
